import React, { useMemo } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Navigate, useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { getTerminalOperations } from '../../helpers/terminalOperations';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import {
  dashboardGradientSx,
  operationCardSx,
  terminalColors,
  terminalExitButtonSx,
} from '../../theme/terminalStyles';

const opIcons = {
  picking: <Inventory2OutlinedIcon sx={{ fontSize: 40 }} />,
  stocktake: <FactCheckOutlinedIcon sx={{ fontSize: 40 }} />,
  price: <SellOutlinedIcon sx={{ fontSize: 40 }} />,
  incomingAiInvoices: <ReceiptLongOutlinedIcon sx={{ fontSize: 40 }} />,
  docConfirmation: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 40 }} />,
  labelPrinting: <LocalOfferOutlinedIcon sx={{ fontSize: 40 }} />,
};

const pageSx = {
  ...dashboardGradientSx,
  display: 'flex',
  flexDirection: 'column',
};

/** Однаковий відступ зверху й зліва для «Вихід» (трохи вище до верху екрана) */
const exitInset = { xs: 1.5, sm: 2 };

/** Ширші бокові поля для карток операцій */
const operationsInsetX = { xs: 4, sm: 4.5 };

const Dashboard = () => {
  const navigate = useNavigate();
  const { authState, logoutUser } = useAuth();
  const user = authState.user;

  const ops = useMemo(() => getTerminalOperations(user), [user]);

  if (ops.length === 1) {
    return <Navigate to={ops[0].path} replace />;
  }

  const handleExit = () => {
    logoutUser();
    navigate(routes.login);
  };

  if (ops.length === 0) {
    return (
      <Box sx={pageSx}>
        <Box
          sx={{
            flexShrink: 0,
            pt: exitInset,
            pl: exitInset,
            pr: exitInset,
            pb: 0.5,
          }}
        >
          <Button
            variant="contained"
            disableElevation
            startIcon={<ArrowBackIcon />}
            onClick={handleExit}
            sx={terminalExitButtonSx}
          >
            Вихід
          </Button>
        </Box>
        <Box sx={{ px: operationsInsetX, flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Paper sx={{ p: 3, mt: 2, borderRadius: 2, textAlign: 'center', boxSizing: 'border-box' }}>
            <Typography variant="h6" gutterBottom>
              У вас недостатньо прав для роботи в системі
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Зверніться до адміністратора, щоб увімкнути операції для цього терміналу.
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box
        sx={{
          flexShrink: 0,
          pt: exitInset,
          pl: exitInset,
          pr: exitInset,
          pb: 0.5,
        }}
      >
        <Button
          variant="contained"
          disableElevation
          startIcon={<ArrowBackIcon />}
          onClick={handleExit}
          sx={terminalExitButtonSx}
        >
          Вихід
        </Button>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          px: operationsInsetX,
          pb: 2,
          gap: 2,
          width: '100%',
          maxWidth: 480,
          mx: 'auto',
          boxSizing: 'border-box',
        }}
      >
        {ops.map((op) => (
          <Paper
            key={op.key}
            elevation={0}
            onClick={() => navigate(op.path)}
            sx={operationCardSx}
          >
            <Box sx={{ color: terminalColors.textPrimary, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {opIcons[op.key] || opIcons.picking}
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: terminalColors.textPrimary,
                fontSize: { xs: '1.05rem', sm: '1.25rem' },
              }}
            >
              {op.label}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default Dashboard;
